import { useEffect, useMemo, useState } from "react";
import axiosInstance from "../../config/axiosApi/axiosApi.jsx";
import APIs from "../../Constants/apis.js";
import CustomModal from "../../components/CustomComponents/modal/customModal.jsx";
import Preloader from "../../components/CustomComponents/preLoad/preLoader.js";
import showErrorToast from "../../components/CustomComponents/toast/toast.js";
import useThemeColor from "../../hooks/useThemeColor.js";
import "./riderWalletDrawer.css";

const formatMoney = (n) =>
  Number(n || 0).toLocaleString(undefined, {
    minimumFractionDigits: 2,
    maximumFractionDigits: 2,
  });

const formatWhen = (value) => {
  if (!value) return "—";
  const d = new Date(value);
  if (Number.isNaN(d.getTime())) return "—";
  return d.toLocaleString(undefined, {
    day: "2-digit",
    month: "short",
    year: "numeric",
    hour: "2-digit",
    minute: "2-digit",
  });
};

const ENTRY_LABELS = {
  EarningDelivery: "Delivery fee",
  EarningTip: "Tip",
  Payout: "Payout",
  CodLiability: "COD collected",
  CodRemittancePaid: "COD remittance",
};

const entryLabel = (type) => ENTRY_LABELS[type] || type || "—";

const bucketMeta = (bucket) => {
  if (bucket === "EarningsPayable") {
    return { label: "Earnings", className: "rw-pill rw-pill--earn" };
  }
  if (bucket === "CodOwed") {
    return { label: "COD", className: "rw-pill rw-pill--cod" };
  }
  return { label: bucket || "—", className: "rw-pill rw-pill--muted" };
};

const RiderWalletDrawer = ({ riderId, riderName, isOpen, onClose }) => {
  const themeColor = useThemeColor();
  const [loading, setLoading] = useState(false);
  const [wallet, setWallet] = useState(null);
  const [bucketFilter, setBucketFilter] = useState("all");

  useEffect(() => {
    if (!isOpen || !riderId) return;
    setBucketFilter("all");
    const load = async () => {
      try {
        setLoading(true);
        const response = await axiosInstance.get(APIs.GET_RIDER_WALLET, {
          params: { MarketplaceRider_ID: riderId, RecentLedgerCount: 40 },
        });
        if (response.data.status) {
          setWallet(response.data.data);
        } else {
          showErrorToast(response.data.message || "Failed to load wallet");
        }
      } catch {
        showErrorToast("Failed to load wallet");
      } finally {
        setLoading(false);
      }
    };
    load();
  }, [isOpen, riderId]);

  const ledger = useMemo(() => {
    const rows = wallet?.recentLedger || [];
    if (bucketFilter === "all") return rows;
    return rows.filter((e) => (e.bucket || e.Bucket) === bucketFilter);
  }, [wallet, bucketFilter]);

  const earnings = Number(wallet?.earningsPayable ?? wallet?.EarningsPayable ?? 0);
  const codOwed = Number(wallet?.codOwed ?? wallet?.CodOwed ?? 0);

  return (
    <CustomModal
      title={`Wallet — ${riderName || `Rider #${riderId}`}`}
      subtitle="Earnings the platform owes this rider, and COD they still owe back."
      isOpen={isOpen}
      onClose={onClose}
      isCentered
      modalSize="modal-lg"
      maxHeight="90vh"
      showButton={false}
      showSubmitButton={false}
      showCancelButton={true}
    >
      {loading && <Preloader />}
      {wallet && (
        <div className="rw-wallet">
          <div className="rw-summary-row">
            <div className="rw-summary-card rw-summary-card--earn">
              <div className="rw-summary-top">
                <span className="rw-summary-icon" style={{ backgroundColor: `${themeColor}22`, color: themeColor }}>
                  <i className="fa fa-arrow-down" />
                </span>
                <span className="rw-summary-tag">Platform → Rider</span>
              </div>
              <div className="rw-summary-label">Earnings payable</div>
              <div className="rw-summary-value" style={{ color: themeColor }}>
                {formatMoney(earnings)}
              </div>
              <div className="rw-summary-hint">Delivery fees + tips not yet paid out</div>
            </div>

            <div className="rw-summary-card rw-summary-card--cod">
              <div className="rw-summary-top">
                <span className="rw-summary-icon rw-summary-icon--cod">
                  <i className="fa fa-arrow-up" />
                </span>
                <span className="rw-summary-tag rw-summary-tag--cod">Rider → Platform</span>
              </div>
              <div className="rw-summary-label">COD owed</div>
              <div className="rw-summary-value rw-summary-value--cod">{formatMoney(codOwed)}</div>
              <div className="rw-summary-hint">Cash collected, awaiting remittance</div>
            </div>
          </div>

          <div className="rw-ledger-head">
            <div>
              <h6 className="rw-ledger-title mb-0">Recent ledger</h6>
              <div className="rw-ledger-sub text-muted small">Latest wallet movements for this rider</div>
            </div>
            <div className="rw-filter-pills">
              {[
                { value: "all", label: "All" },
                { value: "EarningsPayable", label: "Earnings" },
                { value: "CodOwed", label: "COD" },
              ].map((opt) => (
                <button
                  key={opt.value}
                  type="button"
                  className={`rw-filter-pill ${bucketFilter === opt.value ? "is-active" : ""}`}
                  style={
                    bucketFilter === opt.value
                      ? { backgroundColor: themeColor, borderColor: themeColor }
                      : undefined
                  }
                  onClick={() => setBucketFilter(opt.value)}
                >
                  {opt.label}
                </button>
              ))}
            </div>
          </div>

          <div className="table-responsive rw-table-wrap">
            <table className="table table-hover rw-table mb-0">
              <thead>
                <tr>
                  <th>When</th>
                  <th>Type</th>
                  <th>Bucket</th>
                  <th className="text-end">Amount</th>
                  <th className="text-end">Balance</th>
                  <th>Order</th>
                </tr>
              </thead>
              <tbody>
                {ledger.length === 0 ? (
                  <tr>
                    <td colSpan={6} className="rw-empty text-center text-muted py-4">
                      <i className="fa fa-inbox d-block mb-2" style={{ fontSize: 22, opacity: 0.45 }} />
                      No ledger entries{bucketFilter !== "all" ? " for this filter" : ""}
                    </td>
                  </tr>
                ) : (
                  ledger.map((e) => {
                    const type = e.entryType || e.EntryType;
                    const bucket = e.bucket || e.Bucket;
                    const amount = Number(e.amount ?? e.Amount ?? 0);
                    const balance = e.balanceAfterBucket ?? e.BalanceAfterBucket;
                    const invoice = e.invoiceNo || e.Recvorder_InvoiceNo || e.invoiceNO;
                    const meta = bucketMeta(bucket);
                    const amountClass =
                      amount < 0 ? "rw-amt rw-amt--neg" : amount > 0 ? "rw-amt rw-amt--pos" : "rw-amt";

                    return (
                      <tr key={e.ledgerId || e.Ledger_ID}>
                        <td className="rw-when">{formatWhen(e.createdAt || e.CreatedAt)}</td>
                        <td>
                          <div className="rw-type">{entryLabel(type)}</div>
                          {ENTRY_LABELS[type] ? (
                            <div className="rw-type-code text-muted">{type}</div>
                          ) : null}
                        </td>
                        <td>
                          <span className={meta.className}>{meta.label}</span>
                        </td>
                        <td className={`text-end ${amountClass}`}>{formatMoney(amount)}</td>
                        <td className="text-end rw-balance">{formatMoney(balance)}</td>
                        <td>
                          {invoice ? (
                            <span className="rw-invoice" title={invoice}>
                              {invoice}
                            </span>
                          ) : (
                            <span className="text-muted">—</span>
                          )}
                        </td>
                      </tr>
                    );
                  })
                )}
              </tbody>
            </table>
          </div>
        </div>
      )}
    </CustomModal>
  );
};

export default RiderWalletDrawer;
